import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BlogNewsletter from "@/components/BlogNewsletter";
import CommentSection from "@/components/CommentSection";
import { fetchBlogPost } from "@/lib/api";
import { BLOG } from "@/constants/testIds";
import { CANONICAL_ORIGIN } from "@/lib/seo";

const ALLOWED_TAGS = new Set(["h2", "h3", "p", "ul", "li", "strong", "em", "br", "a"]);

function sanitizeHtml(html) {
  if (!html) return "";
  let cleaned = html.replace(/<(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, "");
  cleaned = cleaned.replace(/<\/?([a-zA-Z0-9]+)([^>]*)>/g, (match, tag, attrs) => {
    const lower = tag.toLowerCase();
    if (!ALLOWED_TAGS.has(lower)) return "";
    const isClosing = match.startsWith("</");
    if (isClosing) return `</${lower}>`;
    if (lower === "a") {
      const hrefMatch = /href\s*=\s*"(https?:\/\/[^"]+)"/i.exec(attrs || "");
      if (!hrefMatch) return "";
      return `<a href="${hrefMatch[1]}" target="_blank" rel="noopener noreferrer nofollow">`;
    }
    return `<${lower}>`;
  });
  return cleaned;
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    (async () => {
      try {
        const data = await fetchBlogPost(slug);
        setPost(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen text-white bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 mb-4">Post not found.</p>
          <Link to="/blog" className="text-white hover:underline">Back to blog</Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-screen bg-[#050505]" />;
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description,
    image: post.cover_image_url || undefined,
    datePublished: post.created_at,
    author: { "@type": "Organization", name: "Skilmark" },
  };

  const canonicalSlug = post.canonical_slug || post.slug;

  return (
    <div className="relative min-h-screen text-white">
      <Helmet>
        <title>{`${post.title} | Skilmark Blog`}</title>
        <meta name="description" content={post.meta_description} />
        <link rel="canonical" href={`${CANONICAL_ORIGIN}/blog/${canonicalSlug}`} />
        {post.canonical_slug && <meta name="robots" content="noindex, follow" />}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:type" content="article" />
        {post.cover_image_url && <meta property="og:image" content={post.cover_image_url} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />

      <article data-testid="blog-post-page" className="pt-32 pb-24 px-6 lg:px-12 max-w-3xl mx-auto">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft size={16} /> Back to blog
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex gap-2 mb-5 flex-wrap">
            {(post.tags || []).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wider rounded-full border border-white/15 px-2.5 py-0.5 text-white/40">
                {t}
              </span>
            ))}
          </div>
          <h1 data-testid="blog-post-title" className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-4">
            {post.title}
          </h1>
          <p className="text-xs text-white/30 mb-8">
            {new Date(post.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
          </p>

          {post.cover_image_url && (
            <div className="rounded-2xl overflow-hidden mb-10 aspect-[16/9]">
              <img src={post.cover_image_url} alt={post.cover_image_alt || post.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div
            data-testid="blog-post-content"
            className="prose-blog text-white/70 leading-relaxed [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-white [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-2 [&_strong]:text-white [&_a]:text-white [&_a]:underline [&_a]:decoration-white/40 [&_a]:hover:decoration-white"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content_html) }}
          />
        </motion.div>

        {post.related_posts && post.related_posts.length > 0 && (
          <div data-testid={BLOG.relatedPostsSection} className="mt-16 pt-10 border-t border-white/10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-6">Related Reads</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {post.related_posts.map((rp) => (
                <Link
                  key={rp.slug}
                  to={`/blog/${rp.slug}`}
                  data-testid={BLOG.relatedPostCard(rp.slug)}
                  className="tracing-border glass-panel rounded-xl overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300"
                >
                  {rp.cover_image_url && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={rp.cover_image_url} alt={rp.cover_image_alt || rp.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4">
                    <p className="font-display text-sm font-bold leading-snug">{rp.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <CommentSection slug={post.slug} />

        <BlogNewsletter />
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
